import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { athleteDisplayName } from '@strength/domain';
import {
  getMetricSpec,
  removeUnits,
  WorkoutExercise,
  WorkoutSetMetric,
} from '@strength/domain-workout';
import { MdSwapHoriz } from '@teamworksdev/icons';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { trpc } from '../../../../../../trpc/react';
import {
  DrawerFooter,
  DrawerHeader,
  TabletDrawer,
} from '../../../../../components/drawer/drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../../components/form/form';
import { useWorkoutContext } from '../../context/WorkoutContext';
import { SectionExercises } from '../../../types';
import { AthleteWithTeam } from '../../types';
import { ExerciseInput } from '../tablet-inputs/ExerciseInput';
import { ExerciseOrderInput } from './ExerciseOrderInput';
import { TabletMetricsFormInput } from './TabletMetricsFormInput';
import {
  createEditExerciseTabletSchema,
  ExerciseFormData,
  ExerciseSectionEntry,
} from './types';
import { initializeMetrics, shouldDisplayValueAsIs } from './utils';

type TabletCreateEditExerciseSheetProps = {
  open: boolean;
  athlete: AthleteWithTeam;
  workouts: SectionExercises[];
  editExercise?: WorkoutExercise;
  onSave: (exerciseData: ExerciseFormData) => void;
  onClose: () => void;
};

export const NEW_EXERCISE_WORKOUT_ID = 'NEW_EXERCISE_WORKOUT_ID';

export function TabletCreateEditExerciseSheet({
  open,
  athlete,
  workouts,
  editExercise,
  onSave,
  onClose,
}: TabletCreateEditExerciseSheetProps) {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();
  const isEdit = !!editExercise;

  const { t } = useTranslation();
  const { getMetric, getUnitsForMetric } = useWorkoutContext();
  const exerciseEditLibraryData = trpc.exerciseLibrary.exerciseById.useQuery(
    {
      libraryExerciseId: editExercise?.libraryExerciseId ?? '',
    },
    {
      enabled: !!editExercise?.libraryExerciseId,
    },
  );

  const form = useForm<z.infer<typeof createEditExerciseTabletSchema>>({
    resolver: zodResolver(createEditExerciseTabletSchema),
  });
  const { handleSubmit, control, setValue, getValues, watch } = form;

  // watch for selected exercise changes
  const selectedExercise = watch('exercise');
  const prevSelectedExerciseRef = useRef<typeof selectedExercise | null>(null);

  const handleCloseSheet = () => {
    form.reset();
    prevSelectedExerciseRef.current = null;
    onClose();
  };

  const onFormSubmit = (
    data: z.infer<typeof createEditExerciseTabletSchema>,
  ) => {
    let hasError = false;
    let errorMessage = '';
    data.metrics.forEach((metric) => {
      const spec = getMetricSpec(getMetric(metric.metricId));
      metric.inputsForSet.forEach((input) => {
        if (
          !spec.schema.safeParse(
            input.unit ? `${input.value}${input.unit}` : input.value,
          ).success
        ) {
          if (input.value.length === 0) {
            // if the value is empty, but a unit is selected, clear the unit and don't throw an error
            input.unit = '';
            return;
          }
          hasError = true;
          errorMessage = t('invalidMetricValue');
        }
      });
    });

    if (hasError) {
      form.setError('metrics', { type: 'custom', message: errorMessage });
      return;
    }

    onSave(data);
    handleCloseSheet();
  };

  // when editing an exercise we need to initialize the form with the exerciselibrary data
  // and the metrics associated with the exercise
  useEffect(() => {
    if (isEdit && exerciseEditLibraryData.data) {
      const exercise = exerciseEditLibraryData.data;
      setValue('exercise', exercise);
      setValue('isEachSide', editExercise?.isEachSide ?? false);

      const metrics = editExercise?.metrics.map((m) => {
        const metric = getMetric(m.metricId);
        const spec = getMetricSpec(metric);

        return {
          ...m,
          name: m.metricName,
          type: m.metricType,
          exerciseMetricId: m.workoutExerciseMetricId,
          inputsForSet: editExercise.sets.map((set) => {
            const setMetric: WorkoutSetMetric = set.metrics.find(
              (sm) => sm.metricId === m.metricId,
            ) ?? {
              metricId: '',
              input: '',
              forCollection: false,
            };

            const parseResult = spec.schema.safeParse(setMetric.input);

            if (parseResult.success) {
              const value = spec.displayValue(parseResult.data);
              return {
                // show tempo/keywords/complex reps as-is (otherwise we may strip
                // meaningful characters, e.g. X's from tempo, from them)
                value: shouldDisplayValueAsIs(
                  m.metricType,
                  value,
                  metric.config.keywords,
                  parseResult.data.type,
                )
                  ? value
                  : removeUnits(value),
                unit:
                  parseResult.data.format === 'percentage'
                    ? '%'
                    : parseResult.data.unit,
                setIndex: set.sortOrder,
                forCollection: setMetric.forCollection,
                setId: set.workoutSetId,
                workoutSetGroupId: set.workoutSetGroupId,
              };
            }

            return {
              value: '',
              unit: undefined,
              setIndex: set.sortOrder,
              forCollection: setMetric.forCollection,
              setId: set.workoutSetId,
              workoutSetGroupId: set.workoutSetGroupId,
            };
          }),
        };
      });

      setValue('metrics', metrics);
    }
  }, [isEdit, editExercise, setValue, exerciseEditLibraryData.data, getMetric]);

  // set the default metrics inputs once an exercise is selected/swapped
  // set the selected exercise as the last exercise in the workout order
  useEffect(() => {
    if (
      selectedExercise &&
      prevSelectedExerciseRef.current !== selectedExercise
    ) {
      const metrics = getValues('metrics');
      setValue(
        'metrics',
        initializeMetrics(
          selectedExercise.defaultMetrics,
          metrics,
          getUnitsForMetric,
          isEdit,
        ),
      );

      const workoutsBySection = workouts.map((section) => ({
        workoutSectionId: section.sectionId,
        sectionName: section.sectionName,
        entries: [
          ...section.exerciseWorkouts.reduce((acc, exercise) => {
            const isEditExercise =
              exercise.workoutExerciseId === editExercise?.workoutExerciseId;
            return [
              ...acc,
              {
                workoutExerciseId: exercise.workoutExerciseId,
                exerciseSortOrder: exercise.exerciseSortOrder,
                supersetSortOrder: exercise.supersetSortOrder,
                exerciseName: isEditExercise
                  ? selectedExercise.name
                  : exercise.exerciseName,
                ...(exercise.workoutSupersetId && {
                  workoutSupersetId: exercise.workoutSupersetId,
                }),
              },
            ];
          }, [] as ExerciseSectionEntry[]),
          ...(section.sectionId === workouts[workouts.length - 1]?.sectionId &&
          !editExercise
            ? [
                {
                  workoutExerciseId: NEW_EXERCISE_WORKOUT_ID,
                  exerciseSortOrder:
                    (section.exerciseWorkouts[
                      section.exerciseWorkouts.length - 1
                    ]?.exerciseSortOrder ?? -1) + 1,
                  exerciseName: selectedExercise.name,
                },
              ]
            : []),
        ],
      }));

      setValue('exerciseOrder', workoutsBySection);
      prevSelectedExerciseRef.current = selectedExercise;
    }
  }, [getValues, workouts, selectedExercise, setValue, isEdit, editExercise]);

  return (
    <TabletDrawer open={open}>
      <DrawerHeader
        onClose={handleCloseSheet}
        className="border-b border-gray-300"
      >
        <Typography variant="h2">
          {isEdit ? t('editExercise') : t('addExercise')}
        </Typography>
        <Typography variant="body2" color={mediumEmphasisTextLight}>
          {athleteDisplayName(athlete)}
        </Typography>
      </DrawerHeader>
      <Form {...form}>
        <form
          id="tablet-exercise-form"
          onSubmit={handleSubmit(onFormSubmit)}
          className="flex flex-col grow overflow-hidden"
        >
          <div className="flex flex-col grow gap-8 p-6 overflow-y-scroll overflow-x-hidden">
            <FormField
              control={control}
              name="exercise"
              render={({ field: { ref, ...field } }) => (
                <FormItem>
                  <FormControl className="flex gap-2">
                    {isEdit ? (
                      <Box
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}
                      >
                        <TextField
                          label={t('newExercise')}
                          value={editExercise?.exerciseName}
                          disabled
                          size="medium"
                          sx={{
                            '& .MuiInputBase-formControl': {
                              paddingLeft: '12px',
                              height: '44px',
                              minWidth: '300px',
                            },
                          }}
                        />
                        <Box sx={{ alignContent: 'center' }}>
                          <MdSwapHoriz size={24} className="text-black/60" />
                        </Box>
                        <ExerciseInput
                          {...field}
                          ref={ref}
                          teamId={athlete.teamId}
                          placeholder={t('exerciseName')}
                          autoFocus
                        />
                      </Box>
                    ) : (
                      <ExerciseInput
                        {...field}
                        ref={ref}
                        teamId={athlete.teamId}
                        placeholder={t('exerciseName')}
                        autoFocus
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {selectedExercise && (
              <div className="w-fit">
                <div className="flex flex-row gap-4 items-center justify-between">
                  <Typography variant="h3">{selectedExercise.name}</Typography>
                  <FormField
                    control={control}
                    name="isEachSide"
                    render={({ field: { value, onChange } }) => (
                      <FormItem className="flex flex-row gap-1 items-center">
                        <Switch
                          size="small"
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <Typography variant="body2Medium">
                          {t('setPerSide')}
                        </Typography>
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="metrics"
                  render={({ field: { value, onChange } }) => {
                    if (!value) {
                      return <></>;
                    }
                    return (
                      <FormItem>
                        <FormControl className="flex flex-col gap-4 mt-3 min-w-0">
                          <TabletMetricsFormInput
                            form={form}
                            value={value}
                            onChange={onChange}
                            sets={editExercise?.sets}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                {!isEdit && (
                  <FormField
                    control={control}
                    name="exerciseOrder"
                    render={({ field: { value, onChange } }) => {
                      if (!value) {
                        return <></>;
                      }
                      return (
                        <FormItem className="mt-8">
                          <FormLabel className="font-semibold text-xl text-black">
                            {t('exerciseOrder')}
                          </FormLabel>
                          <FormControl className="flex flex-col gap-4  min-w-0">
                            <ExerciseOrderInput
                              onChange={onChange}
                              value={value}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex border-t border-gray-300 py-8 px-6 justify-end gap-2 w-full">
        <Button variant="outlined" onClick={handleCloseSheet}>
          {t('cancel')}
        </Button>
        <Button type="submit" form="tablet-exercise-form">
          {t('saveChanges')}
        </Button>
      </DrawerFooter>
    </TabletDrawer>
  );
}
